/**
 * Feature Table Component
 *
 * Displays the list of admin-managed devcontainer features
 * with active toggle, gated badge, and edit actions.
 */

import { useState, type ReactElement } from 'react';
import type { Feature } from '../../schemas/admin.schemas';
import { FeatureVersion } from './FeatureVersion';
import { RuntimeConfigButton } from './RuntimeConfigButton';

interface FeatureTableProps {
  features: Feature[];
  onEdit: (feature: Feature) => void;
  onToggleActive: (feature: Feature) => void;
}

export function FeatureTable({
  features,
  onEdit,
  onToggleActive,
}: FeatureTableProps): ReactElement {
  const [showInactive, setShowInactive] = useState(false);

  const filtered = showInactive
    ? features
    : features.filter((f) => f.is_active);

  return (
    <div className="feature-table">
      <div className="feature-table__controls">
        <label className="feature-table__checkbox-label">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => { setShowInactive(e.target.checked); }}
          />
          Show inactive
        </label>
      </div>

      <table className="feature-table__table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Source</th>
            <th>Version</th>
            <th>Category</th>
            <th>Gated</th>
            <th>Active</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={7} className="feature-table__empty">
                No features found.
              </td>
            </tr>
          ) : (
            filtered.map((feature) => (
              <tr
                key={feature.id}
                className={
                  feature.is_active ? '' : 'feature-table__row--inactive'
                }
              >
                <td>
                  <div className="feature-table__name">
                    {feature.display_name}
                  </div>
                  <div className="feature-table__subtext">{feature.name}</div>
                </td>
                <td className="feature-table__source">{feature.source}</td>
                <td className="feature-table__version">
                  <FeatureVersion feature={feature} />
                </td>
                <td>{feature.category}</td>
                <td>
                  {feature.is_gated && (
                    <span
                      className="feature-table__badge"
                      title={`${feature.gate_attribute ?? ''} = ${feature.gate_value ?? ''}`}
                    >
                      {feature.gate_value ?? 'Gated'}
                    </span>
                  )}
                </td>
                <td>
                  <button
                    type="button"
                    className={`feature-table__toggle ${feature.is_active ? 'feature-table__toggle--active' : ''}`}
                    onClick={() => { onToggleActive(feature); }}
                    aria-label={`${feature.is_active ? 'Deactivate' : 'Activate'} ${feature.display_name}`}
                  >
                    {feature.is_active ? 'Active' : 'Inactive'}
                  </button>
                </td>
                <td>
                  <div className="feature-table__actions-cell">
                    <button
                      type="button"
                      className="feature-table__edit-btn"
                      onClick={() => { onEdit(feature); }}
                      aria-label={`Edit ${feature.display_name}`}
                    >
                      Edit
                    </button>
                    <RuntimeConfigButton
                      featureId={feature.id}
                      featureName={feature.display_name}
                    />
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
